import { canSkipStep, isSetupSkipped } from './setupSkip';
import { isAdultSelfAttestedUser as attestedUser } from './adultSelfAttest';
import { expectsFreeFor } from './promoFirstFree';

// Sam 10:33 AM Oct 23 (TEST): first free Talk Now needs only the 18+ box, so the
// checkout gate lets an 18+-confirmed talker through the trust step for that
// checkout only. The server re-checks (one free per talker; paid keeps ID + card).
const freeTalkNowCheckout = () => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem('CheckoutPage') || window.localStorage.getItem('CheckoutPage');
    const hint = window.sessionStorage.getItem('sl_free_now_intent');
    if (hint && expectsFreeFor(hint, 'chat')) return true;
    const at = Number(window.localStorage.getItem('sl_free_now_intent'));
    const freshTalkNow = at > 0 && Date.now() - at < 3 * 60 * 60 * 1000;
    const pd = JSON.parse(raw || '{}') || {};
    const od = pd.orderData || {};
    if (od.startNow !== true || od.declineFree === true) return false;
    return od.firstSessionFree === true || freshTalkNow || expectsFreeFor(pd.listing?.id?.uuid, od.sessionFormat);
  } catch (e) {
    return false;
  }
};
/**
 * Strict onboarding order on the client (Sam, Oct 7 2026). The server
 * (/api/onboarding/status + server/api-util/onboardingGuard.js) is the source
 * of truth; this file maps steps to pages and decides which pages a person
 * may open while a step is still unfinished.
 *
 * ID check later (Sam, Oct 13 2026): easy, investing steps first, then the
 * ID check right before booking / going Available, then the email code.
 * Talker:   guidelines → talkProfile → card → identity → email
 * Listener: guidelines → listenProfile → availability → identity → payouts → email
 * No role:  guidelines → role → identity → email
 * Email verification is the LAST step (Sam, Oct 11 2026); it is finished on /onboarding.
 */

export const STEP_EMAIL = 'email';
export const STEP_GUIDELINES = 'guidelines';
export const STEP_IDENTITY = 'identity';
export const STEP_ROLE = 'role';
export const STEP_CARD = 'card';
export const STEP_TRUST = 'trust';
export const STEP_PAYOUTS = 'payouts';
export const STEP_TALK_PROFILE = 'talkProfile';
export const STEP_LISTEN_PROFILE = 'listenProfile';
export const STEP_AVAILABILITY = 'availability';

/**
 * Friendly copy (no vendor names). `page` is where the step is finished;
 * null means it is finished right on the onboarding page.
 */
export const ONBOARDING_STEP_INFO = {
  [STEP_EMAIL]: {
    label: 'Verify email',
    why: 'So we can reach you about your sessions.',
    title: 'Check your email',
    body:
      'Enter the 6-digit code we just emailed you. Your account goes live as soon as it’s confirmed.',
    cta: 'Check your inbox',
    page: null,
  },
  [STEP_GUIDELINES]: {
    label: 'Guidelines',
    why: 'So every conversation feels kind and safe for both of you.',
    title: 'Our Community Guidelines',
    body:
      'We’re here for kind, respectful conversation. Please read and agree to our Community Guidelines.',
    cta: 'Continue',
    page: null,
  },
  [STEP_IDENTITY]: {
    label: 'ID check',
    why: 'This keeps everyone here a real, verified adult.',
    encourage: 'Almost there! One quick check and you’re ready.',
    title: 'Quick ID check',
    body:
      'A photo ID and a selfie confirm you’re 18+ and that you are who you say you are. It takes about two minutes.',
    cta: 'Start ID check',
    page: null,
  },
  [STEP_ROLE]: {
    label: 'Talk or listen',
    why: 'So we show you the right setup for you.',
    title: 'How do you want to use Sweet Listeners?',
    body: 'Choose whether you want to talk, listen, or both.',
    cta: 'Choose',
    page: { name: 'ChooseAccountRolePage' },
  },
  [STEP_CARD]: {
    label: 'Payment',
    why: 'So booking is quick whenever you’re ready to talk.',
    title: 'Add a payment method',
    body: 'Add a card so you can book a conversation when you’re ready.',
    cta: 'Add a card',
    page: { name: 'StripePayoutPage' },
  },
  [STEP_TRUST]: {
    label: 'Safety check',
    why: 'This keeps every conversation safe and kind for both of you.',
    title: 'One quick safety step',
    body: 'Confirm you’re 18 or older. That’s all you need to talk now for free. A card or ID is only needed to book for later or a paid session.',
    cta: 'Continue',
    page: null,
  },
  [STEP_PAYOUTS]: {
    label: 'Get paid',
    why: 'So you get paid on time for every session, securely.',
    title: 'Set up how you get paid',
    body: 'Add your payout details and bank account so you can get paid for listening.',
    cta: 'Set up payouts',
    page: { name: 'StripePayoutPage', to: { search: '?view=payouts' } },
  },
  [STEP_TALK_PROFILE]: {
    label: 'Profile',
    title: 'Finish your profile',
    why: 'Nobody needs your real name here.',
    privacyNote:
      'Your profile is private. Listeners can’t browse or search for you. You choose whether the listener you book with can see it.',
    body: 'Pick the screen name listeners will see.',
    cta: 'Edit profile',
    page: { name: 'ProfileSettingsPage' },
  },
  [STEP_LISTEN_PROFILE]: {
    label: 'Profile',
    get title() {
      return require('./teachIntent').hasTeachIntent() ? 'Set up teaching' : 'Publish your profile';
    },
    get why() {
      return require('./teachIntent').hasTeachIntent()
        ? 'So learners can find what you teach.'
        : 'So people can find you and book a time.';
    },
    get body() {
      return require('./teachIntent').hasTeachIntent()
        ? 'Add a screen name and photo, pick the skills you teach and set your prices.'
        : 'Add a few lines about you, then choose Listening, Teaching or both. You’re almost ready to start earning.';
    },
    get cta() {
      return require('./teachIntent').hasTeachIntent() ? 'Set up teaching' : 'Publish your profile';
    },
    page: { name: 'NewListingPage' },
  },
  [STEP_AVAILABILITY]: {
    label: 'Hours',
    why: 'So talkers know when you’re free to listen.',
    title: 'Set your weekly hours',
    body: 'Choose when talkers can book you. You can go Available and take bookings right after.',
    cta: 'Set my hours',
    page: { name: 'AvailabilityPage' },
  },
};

/** Signed-in pages that stay open during any step (account basics, ops). */
const ALWAYS_ALLOWED = new Set([
  'OnboardingPage',
  'EmailVerificationPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'ManageAccountPage',
  'OpsModerationPage',
  'SafetyReviewPage',
  'OpsFoundingPage',
  'OpsPromotionsPage',
  'OpsInsightsPage',
  'OpsIdReviewPage',
  'Styleguide',
  'StyleguideGroup',
  'StyleguideComponent',
  'StyleguideComponentExample',
  'StyleguideComponentExampleRaw',
]);

/** While the email is unverified only these open (change email, log out, delete). */
const EMAIL_STEP_ALLOWED = new Set([
  'EmailVerificationPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'ManageAccountPage',
]);

const LISTENER_PROFILE_PAGES = [
  'NewListingPage',
  'EditListingPage',
  'EditListingStripeOnboardingPage',
  'YourProfilePage',
  'YourProfilePageTab',
];

/** Extra pages a step may use to get itself done. */
const STEP_PAGES = {
  [STEP_GUIDELINES]: [],
  [STEP_IDENTITY]: ['IdentityReturnPage'],
  [STEP_ROLE]: ['ChooseAccountRolePage'],
  // /account/payment-methods redirects to the combined Payments page (/account/payments).
  [STEP_CARD]: ['PaymentMethodsPage', 'StripePayoutPage'],
  [STEP_TRUST]: ['PaymentMethodsPage', 'StripePayoutPage', 'IdentityReturnPage'],
  [STEP_PAYOUTS]: ['StripePayoutPage', 'StripePayoutOnboardingPage'],
  [STEP_TALK_PROFILE]: [
    'ProfileSettingsPage',
    'YourProfilePage',
    'YourProfilePageTab',
    'AccountProfilePage',
  ],
  [STEP_LISTEN_PROFILE]: LISTENER_PROFILE_PAGES,
  [STEP_AVAILABILITY]: [...LISTENER_PROFILE_PAGES, 'AvailabilityPage'],
};

/**
 * Public pages that still need a finished account when signed in (browse /
 * book / Start now). Marketing and help pages stay open to everyone.
 */
const SIGNED_IN_GATED_PUBLIC = new Set([
  'SearchPage',
  'SearchPageWithListingType',
  'ListingBasePage',
  'ListingPage',
  'ListingPageCanonical',
  'ListingBookingPage',
  'ListingBookingPageCanonical',
  'ProfileBasePage',
  'ProfilePage',
]);

/**
 * Should this route be checked against onboarding for a signed-in user?
 *
 * @param {Object} route routeConfiguration entry
 * @returns {boolean}
 */
export const isOnboardingGatedRoute = route =>
  !!route && (route.auth === true || SIGNED_IN_GATED_PUBLIC.has(route.name));

/**
 * Strict `=== false`, so a still-loading user is never bounced.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const isEmailUnverified = currentUser =>
  !!currentUser?.id && currentUser?.attributes?.emailVerified === false;

/**
 * May this route open while `step` is the first unfinished step?
 *
 * @param {string} routeName
 * @param {string|null} step
 * @returns {boolean}
 */
/**
 * The dashboard stays open during setup (Sam, Oct 16 2026, TEST): it shows the
 * "Finish your setup" checklist with a button to the next step. The server
 * still guards every action (booking, Available, accepting).
 */
// Talker funnel (Sam, Oct 21 2026, TEST): Browse, listener profiles and the
// booking page stay open during setup too, so a new talker never loses the
// listener they picked. The server still guards every booking.
const OPEN_DURING_SETUP = new Set([
  'DashboardPage',
  'SearchPage',
  'SearchPageWithListingType',
  'ListingBasePage',
  'ListingPage',
  'ListingPageCanonical',
  'ListingBookingPage',
  'ListingBookingPageCanonical',
  'ProfileBasePage',
  'ProfilePage',
]);

export const isRouteAllowedForStep = (routeName, step) => {
  if (!step) {
    return true;
  }
  if (OPEN_DURING_SETUP.has(routeName)) {
    return true;
  }
  if (step === STEP_EMAIL) {
    return EMAIL_STEP_ALLOWED.has(routeName) || ALWAYS_ALLOWED.has(routeName);
  }
  if (ALWAYS_ALLOWED.has(routeName)) {
    return true;
  }
  return (STEP_PAGES[step] || []).includes(routeName);
};

/**
 * Client backstop for the card step: if the signed-in user's own data already
 * shows a default card, count the card step as done even when the server
 * could not see it yet. Pure; returns the same object when nothing changes.
 *
 * @param {Object|null} status /api/onboarding/status data
 * @param {Object} [currentUser]
 * @returns {Object|null}
 */
export const withClientCardFallback = (status, currentUser) => {
  const hasOwnDefaultCard = !!currentUser?.stripeCustomer?.defaultPaymentMethod?.id;
  if (!status || !hasOwnDefaultCard || status.done?.[STEP_CARD] !== false) {
    return status;
  }
  const done = { ...status.done, [STEP_CARD]: true };
  const steps = Array.isArray(status.steps) ? status.steps : [];
  const current = steps.find(step => !done[step]) || null;
  return { ...status, done, current, allDone: current === null };
};

/**
 * Where to send someone who opened a page they cannot use yet.
 *
 * @param {Object} params
 * @param {Object} params.route
 * @param {Object} [params.currentUser]
 * @param {Object|null} [params.status] /api/onboarding/status data
 * @returns {{ name: string, search?: string }|null} null = allowed
 */
// Sam 9:24 PM Oct 23 (TEST): a booking never routes into earner setup (listen/teach).
// From checkout only the talker steps count; earner setup resumes from the Dashboard.
const TALKER_BOOKING_STEPS = ['guidelines', 'email', 'trust'];
export const talkerBookingStatus = status => {
  if (!status || status.exempt) return status;
  const steps = TALKER_BOOKING_STEPS;
  const done = status.done || {};
  const isDone = id =>
    id === 'trust' && !(id in done)
      ? true
      : done[id] === true || (id === 'trust' && done.identity === true && done.card === true);
  const current = steps.find(id => !isDone(id)) || null;
  return { ...status, steps, current, allDone: current === null, bookingOnly: true };
};

export const onboardingRedirectFor = ({ route, currentUser, status: rawStatus } = {}) => {
  const status0 = withClientCardFallback(rawStatus, currentUser);
  // Sam 11:30 PM Oct 23: every booking/session page uses talker-only steps (dual-role
  // accounts were bounced from their live session /order into earner setup).
  const BOOKING_ROUTES = ['CheckoutPage', 'OrderDetailsPage', 'OrderDetailsPageRedirect', 'ListingBookingPage', 'ListingPage', 'ListingPageCanonical', 'TransactionPage'];
  const status = BOOKING_ROUTES.includes(route?.name) ? talkerBookingStatus(status0) : status0;
  if (!currentUser?.id || !isOnboardingGatedRoute(route)) {
    return null;
  }
  // Email is the last step now: it is just another step finished on /onboarding.
  if (!status || status.allDone) {
    return null;
  }
  const current = status.current;
  if (!current || isRouteAllowedForStep(route.name, current)) {
    return null;
  }
  // Buyers (talk/learn) are never forced into setup while browsing (Sam 10:01 AM);
  // checkout / booking asks for email code, 18+ and card/ID where required.
  if ((status.role === 'talk' || status.bookingOnly) && route.name !== 'CheckoutPage') {
    return null;
  }
  // Sam 8:00 PM: the 18+ confirm satisfies free Talk Now; card/ID are only for paid or later.
  if (route.name === 'CheckoutPage' && ['trust', 'card', 'identity'].includes(current) && attestedUser(currentUser) && freeTalkNowCheckout()) {
    return null;
  }
  if (canSkipStep(status.role, current) && isSetupSkipped(currentUser?.id?.uuid) && route.name !== 'CheckoutPage') {
    return null;
  }
  return { name: 'OnboardingPage', search: `?from=${encodeURIComponent(route.name)}` };
};

/**
 * Progress for the step indicator.
 *
 * @param {Object|null} status
 * @returns {{ total: number, index: number, items: Array<{ id: string, label: string, done: boolean, current: boolean }> }}
 */
export const onboardingProgressItems = status => {
  const steps = Array.isArray(status?.steps) ? status.steps : [];
  const items = steps.map(id => ({
    id,
    label: ONBOARDING_STEP_INFO[id]?.label || id,
    done: status?.done?.[id] === true,
    current: status?.current === id,
  }));
  const index = Math.max(0, steps.indexOf(status?.current));
  return { total: steps.length, index, items };
};
